if (!customElements.get('cart-line-gift-wrap')) {
  customElements.define(
    'cart-line-gift-wrap',
    class CartLineGiftWrap extends HTMLElement {
      constructor() {
        super();

        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 150));
      }

      connectedCallback() {
        this.cleanStaleGiftWrap();
      }

      get input() {
        return this.querySelector('input[type="checkbox"]');
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get lineKey() {
        return this.getAttribute('data-line-key');
      }

      get giftWrapId() {
        return parseInt(this.getAttribute('data-gift-wrap-id') || '0', 10) || 0;
      }

      get mysteryGiftId() {
        return parseInt(this.getAttribute('data-mystery-gift-id') || '0', 10) || 0;
      }

      setLoading(isLoading) {
        const loader = document.getElementById(`Loader-${this.sectionId}-${this.lineKey}`);
        if (loader) loader.hidden = !isLoading;
        if (this.input) this.input.disabled = isLoading;
      }

      getBundledSections() {
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
        return sectionsToBundle;
      }

      giftWrapAttributeKey(lineKey) {
        return `Gift wrap ${lineKey}`;
      }

      hasGiftProperty(item) {
        if (Array.isArray(item.properties)) {
          return item.properties.some(([name, value]) => name === 'Make it a gift' && value === 'Yes');
        }

        return item.properties?.['Make it a gift'] === 'Yes';
      }

      isGiftWrapItem(item) {
        const title = (item.product_title || item.title || '').toLowerCase();
        return item.variant_id === this.giftWrapId
          || item.handle === 'gift-wrap'
          || title === 'gift wrap';
      }

      countGiftWrappedLines(cart, attributes) {
        return cart.items.reduce((count, item) => {
          if (this.isGiftWrapItem(item) || item.variant_id === this.mysteryGiftId) return count;

          const quantity = Math.max(parseInt(item.quantity || '1', 10) || 1, 1);
          const attributeValue = attributes?.[this.giftWrapAttributeKey(item.key)];
          if (attributeValue === 'Yes') return count + quantity;
          if (attributeValue === 'No') return count;
          if (attributes?.['gift-wrapping'] === true || attributes?.['gift-wrapping'] === 'true') {
            return this.hasGiftProperty(item) ? count + quantity : count;
          }
          return count;
        }, 0);
      }

      countVisibleGiftWrappedLines() {
        const scope = this.closest('cart-items') || this.closest('cart-drawer') || this.closest('main-cart') || document;
        return Array.from(scope.querySelectorAll('cart-line-gift-wrap')).reduce((count, element) => {
          const input = element.querySelector('input[type="checkbox"]');
          if (!input?.checked) return count;

          const lineKey = element.getAttribute('data-line-key');
          const quantityInput = lineKey
            ? scope.querySelector(`input[data-index="${CSS.escape(lineKey)}"]`)
            : null;
          const quantity = Math.max(parseInt(quantityInput?.value || '1', 10) || 1, 1);
          return count + quantity;
        }, 0);
      }

      async fetchCart() {
        const response = await fetch(`${theme.routes.cart_url}.js`, { ...theme.utils.fetchConfig('javascript') });
        return response.json();
      }

      async syncGiftWrap(cart, attributes, giftWrappedLinesOverride = null) {
        if (!this.giftWrapId) return cart;

        const sectionsToBundle = this.getBundledSections();
        const giftWrappedLines = giftWrappedLinesOverride ?? this.countGiftWrappedLines(cart, attributes);
        attributes['gift-wrapping'] = giftWrappedLines > 0 ? 'true' : '';

        if (giftWrappedLines === 0) {
          attributes['gift-note'] = '';
        }

        const response = await fetch(`${theme.routes.cart_update_url}`, {
          ...theme.utils.fetchConfig(),
          body: JSON.stringify({
            updates: {
              [this.giftWrapId]: giftWrappedLines
            },
            attributes,
            sections: sectionsToBundle
          })
        });

        return response.json();
      }

      async cleanStaleGiftWrap() {
        if (document.documentElement.dataset.olgcGiftWrapCleanupPending === 'true' || !this.giftWrapId) return;
        document.documentElement.dataset.olgcGiftWrapCleanupPending = 'true';

        try {
          const cart = await this.fetchCart();
          const attributes = { ...(cart.attributes || {}) };
          const giftWrappedLines = this.countGiftWrappedLines(cart, attributes) || this.countVisibleGiftWrappedLines();
          const giftWrapLineQuantity = cart.items.reduce((quantity, item) => (
            this.isGiftWrapItem(item) ? quantity + (parseInt(item.quantity || '0', 10) || 0) : quantity
          ), 0);
          const hasSelectedLine = Object.keys(attributes).some((key) => key.startsWith('Gift wrap ') && attributes[key] === 'Yes')
            || ((attributes['gift-wrapping'] === true || attributes['gift-wrapping'] === 'true') && cart.items.some((item) => this.hasGiftProperty(item)));

          if (giftWrapLineQuantity !== giftWrappedLines || (giftWrapLineQuantity > 0 && !hasSelectedLine)) {
            const parsedState = await this.syncGiftWrap(cart, attributes, giftWrappedLines);
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-line-gift-wrap-cleanup', cart: parsedState });
          }
        }
        catch (error) {
          console.error(error);
        }
        finally {
          delete document.documentElement.dataset.olgcGiftWrapCleanupPending;
        }
      }

      async onChange(event) {
        if (event.target !== this.input) return;

        this.setLoading(true);

        try {
          const cart = await this.fetchCart();
          const attributes = { ...(cart.attributes || {}) };
          attributes[this.giftWrapAttributeKey(this.lineKey)] = event.target.checked ? 'Yes' : 'No';

          const parsedState = await this.syncGiftWrap(cart, attributes);
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-line-gift-wrap', cart: parsedState, target: event.target, line: this.lineKey, name: event.target.getAttribute('name') });
        }
        catch (error) {
          event.target.checked = !event.target.checked;
          this.setLoading(false);
          console.error(error);
        }
      }
    }
  );
}

